import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import tenantApi from '@/api/tenant/TenantApi'
import {
    UserDto,
    UserRoles,
    UserTenantMembershipDto,
} from '@/api/tenant/models/UserDto'
import { Pages } from '@/Pages'
import UserRolePill from '@/components/UserRolePill'
import Button from '@/uiV2/common/form/inputs/Button'

type UsersTabProps = {
    isActive: boolean
}

type UserMembershipSummary = {
    tenantId: string
    tenantName: string
    roles: UserRoles[]
}

type UserRow = {
    id: string
    email: string
    displayName: string
    isActive: boolean
    isProductAdmin: boolean
    memberships: UserMembershipSummary[]
}

const getUserDisplayName = (user: UserDto): string => {
    if (user.name && user.name.trim().length > 0) {
        return user.name
    }
    const pieces = [user.person?.firstName ?? '', user.person?.lastName ?? '']
    const name = pieces
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ')
        .trim()
    if (name.length > 0) {
        return name
    }
    return user.email
}

const buildMembershipSummary = (
    membership: UserTenantMembershipDto,
): UserMembershipSummary | null => {
    if (!membership.tenantId) {
        return null
    }

    return {
        tenantId: membership.tenantId,
        tenantName:
            membership.tenant?.name ?? membership.tenantId ?? 'Unknown tenant',
        roles: membership.roles ?? [],
    }
}

export default function UsersTab({
    isActive,
}: UsersTabProps): React.JSX.Element {
    const [rows, setRows] = useState<UserRow[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [refreshCount, setRefreshCount] = useState(0)
    const [pageNumber, setPageNumber] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const PER_PAGE = 100

    useEffect(() => {
        const handler = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm)
        }, 300)

        return () => {
            window.clearTimeout(handler)
        }
    }, [searchTerm])

    useEffect(() => {
        setPageNumber(1)
    }, [debouncedSearchTerm])

    const load = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const res = await tenantApi.getAllUsers({
                pageNumber,
                perPage: PER_PAGE,
                searchTerm:
                    debouncedSearchTerm.trim().length > 0
                        ? debouncedSearchTerm.trim()
                        : undefined,
            })
            const normalizedRows: UserRow[] = res.users.map((user) => ({
                id: user.id,
                email: user.email,
                displayName: getUserDisplayName(user),
                isActive: user.isActive,
                isProductAdmin: user.isProductAdmin,
                memberships: (user.memberships ?? [])
                    .map(buildMembershipSummary)
                    .filter(
                        (membership): membership is UserMembershipSummary =>
                            membership !== null,
                    ),
            }))

            const sortedRows = normalizedRows.sort((a, b) => {
                const aName = a.displayName.toLowerCase()
                const bName = b.displayName.toLowerCase()
                if (aName === bName) {
                    return a.email.localeCompare(b.email)
                }
                return aName.localeCompare(bName)
            })

            setRows(sortedRows)
            setHasNextPage(res.hasNextPage)
        } catch (err) {
            console.error('Failed to load users', err)
            const message =
                err instanceof Error
                    ? err.message
                    : 'Failed to load users. Please try again.'
            setError(message)
        } finally {
            setIsLoading(false)
        }
    }, [pageNumber, debouncedSearchTerm])

    useEffect(() => {
        if (isActive) {
            void load()
        }
    }, [isActive, load, refreshCount])

    useEffect(() => {
        if (!isLoading && pageNumber > 1 && rows.length === 0) {
            setPageNumber((prev) => Math.max(1, prev - 1))
        }
    }, [isLoading, pageNumber, rows.length])

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <h3 className="page-title">Users</h3>
                <button
                    type="button"
                    className="rounded-full bg-[#153bab] w-12 h-12 flex items-center justify-center text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => setRefreshCount((prev) => prev + 1)}
                    disabled={isLoading}
                    title="Refresh"
                    aria-label="Refresh users"
                >
                    <FontAwesomeIcon icon={faRefresh} />
                </button>
            </div>
            <input
                type="search"
                placeholder="Search by name, email, or tenant..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="form-input min-w-[240px] max-w-lg"
            />

            {error && (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="main-table w-full min-w-[900px]">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Tenants & Roles</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="text-center py-8 text-gray-500"
                                >
                                    Loading users...
                                </td>
                            </tr>
                        )}
                        {!isLoading && rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="text-center py-8 text-gray-500"
                                >
                                    No users found.
                                </td>
                            </tr>
                        )}
                        {!isLoading &&
                            rows.map((row) => (
                                <tr key={row.id} className="hover:bg-slate-50">
                                    <td className="py-3">
                                        <div className="flex flex-col">
                                            <Link
                                                to={`${Pages.ViewUser}?userId=${row.id}`}
                                                className="font-semibold text-gray-900 link-text"
                                            >
                                                {row.displayName}
                                            </Link>
                                            <span className="text-xs text-gray-500">
                                                ID: {row.id}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-3 text-sm text-gray-700">
                                        {row.email}
                                    </td>
                                    <td className="py-3">
                                        <div className="flex flex-col gap-1">
                                            {row.memberships.length === 0 && (
                                                <span className="text-sm text-gray-500">
                                                    No tenant memberships
                                                </span>
                                            )}
                                            {row.memberships.map(
                                                (membership) => (
                                                    <div
                                                        key={`${row.id}-${membership.tenantId}`}
                                                        className="flex flex-wrap items-center gap-2 text-sm"
                                                    >
                                                        <Link
                                                            to={`${Pages.Tenant}?id=${membership.tenantId}`}
                                                            className="link-text"
                                                        >
                                                            {
                                                                membership.tenantName
                                                            }
                                                        </Link>
                                                        <div className="flex flex-wrap gap-1">
                                                            {membership.roles.map(
                                                                (role) => (
                                                                    <UserRolePill
                                                                        key={`${membership.tenantId}-${role}`}
                                                                        value={
                                                                            role
                                                                        }
                                                                    />
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                            {row.isProductAdmin && (
                                                <UserRolePill isProductAdmin />
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 text-sm">
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-1 font-semibold ${
                                                row.isActive
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-700'
                                            }`}
                                        >
                                            {row.isActive
                                                ? 'Active'
                                                : 'Inactive'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <Button
                    type="button"
                    style="secondary"
                    onClick={() =>
                        setPageNumber((prev) => Math.max(1, prev - 1))
                    }
                    enabled={pageNumber > 1 && !isLoading}
                >
                    Previous
                </Button>
                <div>
                    Page {pageNumber} · Showing {rows.length} · Per page{' '}
                    {PER_PAGE}
                </div>
                <Button
                    type="button"
                    style="secondary"
                    onClick={() => setPageNumber((prev) => prev + 1)}
                    enabled={hasNextPage && !isLoading}
                >
                    Next
                </Button>
            </div>
        </div>
    )
}
